import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  getScanStats,
  getSubscriptionStats,
  getSubscriptionSummary,
  getVisitStats,
} from '../../lib/api/adminApi';

const VIEWS = ['day', 'week', 'month', 'year'];
// Sous-granularité (points de la courbe) pour chaque vue.
const SUB = { day: 'hour', week: 'day', month: 'day', year: 'month' };

// Bornes [start, end] de la période + libellé, selon la vue et l'ancre (date de référence).
function periodOf(view, anchor) {
  const d = new Date(anchor);
  let start;
  let end;
  let label;
  if (view === 'day') {
    start = new Date(d);
    start.setHours(0, 0, 0, 0);
    end = new Date(d);
    end.setHours(23, 59, 59, 999);
    label = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } else if (view === 'week') {
    const dow = (d.getDay() + 6) % 7; // 0 = lundi
    start = new Date(d);
    start.setDate(d.getDate() - dow);
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    const o = { day: '2-digit', month: 'short' };
    label = `${start.toLocaleDateString(undefined, o)} – ${end.toLocaleDateString(undefined, o)}`;
  } else if (view === 'month') {
    start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    label = start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } else {
    start = new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
    end = new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
    label = String(d.getFullYear());
  }
  return { start, end, label };
}

function shift(view, anchor, dir) {
  const d = new Date(anchor);
  if (view === 'day') d.setDate(d.getDate() + dir);
  else if (view === 'week') d.setDate(d.getDate() + dir * 7);
  else if (view === 'month') d.setMonth(d.getMonth() + dir);
  else d.setFullYear(d.getFullYear() + dir);
  return d;
}

// Libellé d'un tick de l'axe X selon la vue.
function tickFmt(view, iso) {
  const d = new Date(iso);
  if (view === 'day') return `${d.getHours()}h`;
  if (view === 'week') return d.toLocaleDateString(undefined, { weekday: 'short' });
  if (view === 'month') return String(d.getDate());
  return d.toLocaleDateString(undefined, { month: 'short' });
}

function AdminDashboard({ onAuthError }) {
  const { t } = useTranslation();
  const [view, setView] = useState('day');
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState({ buckets: [], total: 0 });
  const [visitData, setVisitData] = useState({ buckets: [], total: 0, countries: [] });
  const [subData, setSubData] = useState({
    buckets: [],
    totalSubscribed: 0,
    totalCancelRequested: 0,
    totalUnsubscribed: 0,
  });
  const [summary, setSummary] = useState({ active: 0, monthly: 0, annual: 0 });
  const [loading, setLoading] = useState(false);

  const { start, end, label } = useMemo(() => periodOf(view, anchor), [view, anchor]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { granularity: SUB[view], from: start.toISOString(), to: end.toISOString() };
    // allSettled : chaque section est independante. Un endpoint en echec (ex. 500)
    // ne doit pas faire retomber les autres a 0.
    Promise.allSettled([getScanStats(params), getVisitStats(params), getSubscriptionStats(params)])
      .then(([scan, visit, sub]) => {
        if (scan.status === 'fulfilled') {
          setData({ buckets: scan.value.data.buckets || [], total: scan.value.data.total || 0 });
        } else {
          setData({ buckets: [], total: 0 });
        }
        if (visit.status === 'fulfilled') {
          setVisitData({
            buckets: visit.value.data.buckets || [],
            total: visit.value.data.total || 0,
            countries: visit.value.data.countries || [],
          });
        } else {
          setVisitData({ buckets: [], total: 0, countries: [] });
        }
        if (sub.status === 'fulfilled') {
          setSubData({
            buckets: sub.value.data.buckets || [],
            totalSubscribed: sub.value.data.totalSubscribed || 0,
            totalCancelRequested: sub.value.data.totalCancelRequested || 0,
            totalUnsubscribed: sub.value.data.totalUnsubscribed || 0,
          });
        } else {
          setSubData({ buckets: [], totalSubscribed: 0, totalCancelRequested: 0, totalUnsubscribed: 0 });
        }
        // Session expiree : on remonte le 1er 401 rencontre pour repasser au login.
        const authErr = [scan, visit, sub].find(
          (r) => r.status === 'rejected' && r.reason?.response?.status === 401
        );
        if (authErr && onAuthError) onAuthError(authErr.reason);
      })
      .finally(() => setLoading(false));
  }, [view, start, end, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  // Résumé courant (abonnés actifs / mensuel / annuel) — chargé une fois.
  useEffect(() => {
    getSubscriptionSummary()
      .then(({ data: d }) => setSummary({ active: d.active || 0, monthly: d.monthly || 0, annual: d.annual || 0 }))
      .catch((err) => {
        if (onAuthError) onAuthError(err);
      });
  }, [onAuthError]);

  const selectView = (v) => {
    setView(v);
    setAnchor(new Date());
  };

  return (
    <div className="dash">
      <div className="dash__summary">
        <div className="dash__stat dash__stat--active">
          <span className="dash__stat-val">{summary.active}</span>
          <span className="dash__stat-label">{t('admin.active_subs')}</span>
        </div>
        <div className="dash__stat">
          <span className="dash__stat-val">{summary.monthly}</span>
          <span className="dash__stat-label">{t('admin.monthly_subs')}</span>
        </div>
        <div className="dash__stat">
          <span className="dash__stat-val">{summary.annual}</span>
          <span className="dash__stat-label">{t('admin.annual_subs')}</span>
        </div>
      </div>

      <div className="dash__controls">
        <div className="dash__grans">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              className={`dash__gran ${v === view ? 'is-active' : ''}`}
              onClick={() => selectView(v)}
            >
              {t(`admin.gran_${v}`)}
            </button>
          ))}
        </div>
        <div className="dash__nav">
          <button
            type="button"
            className="dash__nav-btn"
            onClick={() => setAnchor((a) => shift(view, a, -1))}
            aria-label="précédent"
          >
            ‹
          </button>
          <span className="dash__period">{label}</span>
          <button
            type="button"
            className="dash__nav-btn"
            onClick={() => setAnchor((a) => shift(view, a, 1))}
            aria-label="suivant"
          >
            ›
          </button>
          <button type="button" className="dash__today" onClick={() => setAnchor(new Date())}>
            {t('admin.today')}
          </button>
        </div>
      </div>

      <div className="dash__kpi">
        <span className="dash__kpi-val">{data.total}</span>
        <span className="dash__kpi-label">{t('admin.total_scans')}</span>
      </div>

      <div className="dash__chart">
        {loading ? (
          <p className="admin__empty">…</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={data.buckets} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="scanFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#19e6d2" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#19e6d2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="start"
                tickFormatter={(iso) => tickFmt(view, iso)}
                tick={{ fill: '#8b96a8', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                minTickGap={8}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: '#8b96a8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                cursor={{ stroke: '#19e6d2', strokeOpacity: 0.3 }}
                contentStyle={{
                  background: '#0f1626',
                  border: '1px solid #1e2a44',
                  color: '#e8edf5',
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
                labelStyle={{ color: '#19e6d2' }}
                labelFormatter={(iso) => new Date(iso).toLocaleString()}
                formatter={(val) => [val, t('admin.scans_word')]}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#19e6d2"
                strokeWidth={2}
                fill="url(#scanFill)"
                dot={{ r: 2, fill: '#19e6d2' }}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <h3 className="dash__section-title">{t('admin.visits_title')}</h3>
      <div className="dash__kpi">
        <span className="dash__kpi-val" style={{ color: '#a78bfa', textShadow: '0 0 18px rgba(167,139,250,0.4)' }}>
          {visitData.total}
        </span>
        <span className="dash__kpi-label">{t('admin.unique_visitors')}</span>
      </div>
      <div className="dash__chart">
        {loading ? (
          <p className="admin__empty">…</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={visitData.buckets} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="visitFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="start"
                tickFormatter={(iso) => tickFmt(view, iso)}
                tick={{ fill: '#8b96a8', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                minTickGap={8}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: '#8b96a8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                cursor={{ stroke: '#a78bfa', strokeOpacity: 0.3 }}
                contentStyle={{
                  background: '#0f1626',
                  border: '1px solid #1e2a44',
                  color: '#e8edf5',
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
                labelStyle={{ color: '#a78bfa' }}
                labelFormatter={(iso) => new Date(iso).toLocaleString()}
                formatter={(val) => [val, t('admin.visitors_word')]}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#a78bfa"
                strokeWidth={2}
                fill="url(#visitFill)"
                dot={{ r: 2, fill: '#a78bfa' }}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {visitData.countries.length > 0 && (
        <div className="dash__countries">
          <h4 className="dash__countries-title">{t('admin.visitors_by_country')}</h4>
          <ul className="dash__country-list">
            {visitData.countries.map((c) => {
              const max = visitData.countries[0].count || 1;
              return (
                <li key={c.countryCode} className="dash__country">
                  <span className="dash__country-name">{c.countryName}</span>
                  <span className="dash__country-bar">
                    <span
                      className="dash__country-fill"
                      style={{ width: `${Math.round((c.count / max) * 100)}%` }}
                    />
                  </span>
                  <span className="dash__country-count">{c.count}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <h3 className="dash__section-title">{t('admin.subs_evolution')}</h3>
      <div className="dash__kpi dash__kpi--dual">
        <span className="dash__dual-item">
          <b className="is-sub">{subData.totalSubscribed}</b> {t('admin.subscribed_word')}
        </span>
        <span className="dash__dual-item">
          <b className="is-cancelreq">{subData.totalCancelRequested}</b> {t('admin.cancel_requested_word')}
        </span>
        <span className="dash__dual-item">
          <b className="is-unsub">{subData.totalUnsubscribed}</b> {t('admin.unsubscribed_word')}
        </span>
      </div>
      <div className="dash__chart">
        {loading ? (
          <p className="admin__empty">…</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={subData.buckets} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="subFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3ee6a0" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#3ee6a0" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cancelReqFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f5a623" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#f5a623" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="unsubFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff3d5e" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#ff3d5e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="start"
                tickFormatter={(iso) => tickFmt(view, iso)}
                tick={{ fill: '#8b96a8', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                minTickGap={8}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: '#8b96a8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                contentStyle={{
                  background: '#0f1626',
                  border: '1px solid #1e2a44',
                  color: '#e8edf5',
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
                labelStyle={{ color: '#19e6d2' }}
                labelFormatter={(iso) => new Date(iso).toLocaleString()}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="subscribed"
                name={t('admin.subscribed_word')}
                stroke="#3ee6a0"
                strokeWidth={2}
                fill="url(#subFill)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="cancelRequested"
                name={t('admin.cancel_requested_word')}
                stroke="#f5a623"
                strokeWidth={2}
                fill="url(#cancelReqFill)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="unsubscribed"
                name={t('admin.unsubscribed_word')}
                stroke="#ff3d5e"
                strokeWidth={2}
                fill="url(#unsubFill)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
